import { useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import { fonts, glass, glassBorder, glassText, brandGradient, palette } from '../styles/overlayTokens';

interface OverlayEntry {
  path: string;
  name: string;
  size: string;
  description: string;
  /** Plain-text setup note rendered below the description (not a warning treatment). */
  note?: string;
}

interface OverlayGroup {
  label: string;
  entries: OverlayEntry[];
}

// Descriptions for /overlay/players, /overlay/side-players, /overlay/timer, /overlay/card,
// /overlay/compare, /overlay/profile/p1, and /overlay/profile/p2 are copied VERBATIM from the
// canonical widget descriptions in packages/web/src/components/pages/cast/widgetData.ts (the
// source of truth for /cast/widgets copy). Do not rewrite these strings independently — if the
// copy needs to change, change it there first and mirror it here.
const OVERLAY_GROUPS: OverlayGroup[] = [
  {
    label: 'Live Match Overlays',
    entries: [
      {
        path: '/overlay/players',
        name: 'Player Widget',
        size: '1920×180',
        description: 'Displays player names, photos, scores, health, and turn indicator with glass styling.',
      },
      {
        path: '/overlay/side-players',
        name: 'Side Players Widget',
        size: '1920×1080',
        description: 'Vertical player panels flanking a transparent center — ideal for side-panel stream layouts.',
      },
      {
        path: '/overlay/timer',
        name: 'Round Timer',
        size: '240×120',
        description: 'Countdown timer for tournament rounds with color-coded warning states.',
      },
      {
        path: '/overlay/card',
        name: 'Card Overlay',
        size: '430×600',
        description: 'Shows a featured card in a branded glass frame with animated entry effects.',
      },
    ],
  },
  {
    label: 'Deck Showcase Scenes',
    entries: [
      {
        path: '/overlay/compare',
        name: 'Deck Compare',
        size: '1920×1080',
        description:
          'Full-screen head-to-head "tale of the tape" comparing both players\' decks — identity, loadout, and stat breakdowns.',
      },
      {
        path: '/overlay/profile/p1',
        name: 'Deck Profile (P1)',
        size: '1920×1080',
        description:
          'Full-screen single-deck showcase for Player 1 — identity, loadout, and stat breakdowns for one deck.',
      },
      {
        path: '/overlay/profile/p2',
        name: 'Deck Profile (P2)',
        size: '1920×1080',
        description:
          'Full-screen single-deck showcase for Player 2 — identity, loadout, and stat breakdowns for one deck.',
      },
    ],
  },
  {
    label: 'Broadcast Canvas',
    entries: [
      {
        path: '/overlay/stream-view',
        name: 'Stream View',
        size: 'Fluid (full canvas)',
        description: 'Composites your webcam feed(s) into the broadcast canvas with branded borders and PiP layout.',
        note: 'Set up from the Caster Console video panel, not Widget Preview.',
      },
    ],
  },
];

function CopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Visually hidden but screen-reader-accessible — carries the aria-live announcement
 *  independently of the visible "Copied" label swap on the button itself. */
const srOnlyStyle = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden' as const,
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
};

function OverlayRow({ path, name, size, description, note }: OverlayEntry) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => clearTimeout(copyTimeoutRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      setCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable (insecure context, permissions) — low-stakes, no
      // user-facing error. Mirrors the web WidgetUrlCopy fallback pattern.
      console.warn('Clipboard API unavailable — unable to copy overlay path');
    }
  };

  const handleFocus = (e: FocusEvent<HTMLButtonElement>) => {
    setFocusVisible(e.currentTarget.matches(':focus-visible'));
  };

  const iconColor = hovered ? glassText.primary : glassText.muted;
  const buttonBg = hovered ? glassBorder.light : 'transparent';
  const pressScale = pressed && !prefersReducedMotion.current ? 'scale(0.96)' : 'scale(1)';

  return (
    <div
      style={{
        padding: 12,
        marginBottom: 8,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: fonts.mono, fontSize: 14, color: glassText.primary }}>{path}</span>
            <span style={{ fontSize: 14, color: glassText.muted }}>{size}</span>
          </div>
          <p style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, marginBottom: 2 }}>{name}</p>
          <p style={{ fontSize: 14, color: glassText.secondary, lineHeight: 1.5 }}>{description}</p>
          {note && <p style={{ fontSize: 14, color: glassText.muted, marginTop: 6 }}>{note}</p>}
        </div>

        <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleCopy}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => {
              setHovered(false);
              setPressed(false);
            }}
            onMouseDown={() => setPressed(true)}
            onMouseUp={() => setPressed(false)}
            onFocus={handleFocus}
            onBlur={() => setFocusVisible(false)}
            aria-label={`Copy path for ${name}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: copied ? 6 : 0,
              minWidth: 44,
              minHeight: 44,
              padding: copied ? '0 14px' : 0,
              borderRadius: 8,
              border: 'none',
              background: buttonBg,
              color: iconColor,
              cursor: 'pointer',
              transform: pressScale,
              transition: 'transform 100ms ease-out, background-color 150ms ease-out, opacity 150ms ease-out',
              outline: focusVisible ? `2px solid ${palette.coralGlow}` : 'none',
              outlineOffset: 2,
            }}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied && <span style={{ fontSize: 14 }}>Copied</span>}
          </button>
          <span aria-live="polite" style={srOnlyStyle}>
            {copied ? 'Path copied to clipboard' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export function OverlayLanding() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '32px 16px',
        background: palette.onyx,
        fontFamily: fonts.body,
        color: glassText.primary,
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: '100%',
          padding: 32,
          background: glass.surfaceHeavy,
          borderRadius: 16,
          border: `1px solid ${glassBorder.light}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top accent bar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: brandGradient.horizontal,
          }}
        />

        <div style={{ marginBottom: 24, paddingTop: 8 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, fontFamily: fonts.display }}>
            Sleeved Overlay System
          </h1>
          <p style={{ fontSize: 14, color: glassText.secondary, lineHeight: 1.6 }}>
            You&apos;ve landed on a Sleeved overlay endpoint — these render inside OBS and are driven live by the
            Caster Console over WebSocket. Open the Caster Console from any game page, then use Widget Preview
            below to get your OBS Browser Source URLs.
          </p>
        </div>

        <a
          href="/cast/widgets"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textAlign: 'center',
            padding: '10px 20px',
            borderRadius: 8,
            background: 'rgba(255,134,92,0.15)',
            border: `1px solid rgba(255,134,92,0.3)`,
            color: '#FF865C',
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
            marginBottom: 24,
            transition: 'background 0.2s',
          }}
        >
          Open Widget Preview
          <span style={srOnlyStyle}> (opens in new tab)</span>
        </a>

        {OVERLAY_GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: 24 }}>
            <h2
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: glassText.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 12,
              }}
            >
              {group.label}
            </h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} role="list">
              {group.entries.map((entry) => (
                <li key={entry.path}>
                  <OverlayRow {...entry} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        <p style={{ fontSize: 14, color: glassText.muted, fontStyle: 'italic' }}>
          Paths only — get your live, session-bound OBS URL from Widget Preview.
        </p>
      </div>
    </div>
  );
}
